<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" @show="onShow" @hidden="onHidden"
                   :title="modalTitle" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" modal-class="mobile-full-screen">


      <div class="row mb-4" v-if="customer">
        <div class="col-md-6">
          <customer-email-address-info :customer="customer"/>
        </div>
        <div class="col-md-6" v-if="model.method === 'deposit'">
          <p v-if="deposit" class="mb-1">
            <span class="mr-2"><b>{{ $t('Available balance') }}:</b></span>
            {{ deposit.available_balance | asCurrency(deposit.currency.symbol) }}
          </p>
          <p v-for="depositReservation in depositReservations" :key="depositReservation.uuid" class="mb-1">
            <span class="mr-2"><b>{{ $t('Reserved for Sale ') }}
              <router-link
                  v-if="depositReservation.auction && !enableContinuousAuction"
                  :to="{ name: 'sales.lots', params: { id: depositReservation.auction.uuid }}">
              {{ depositReservation.auction.sale_no }}
             </router-link>
              <span v-else-if="depositReservation.auction && enableContinuousAuction">
                {{ depositReservation.auction.sale_no }}
              </span>
              <live-sale-ref v-if="depositReservation.auction.live_reference"
                             :href="depositReservation.auction.live_url"
                             :label="depositReservation.auction.live_reference"/>
              : </b></span>
            {{ depositReservation.amount | asCurrency(depositReservation.currency.symbol) }}
          </p>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm invoice-payments-table ">
            <tbody>
            <tr>
              <th class="border-top-0">{{ $t('Invoice') }}</th>
              <th class="border-top-0">{{ $t('Amount') }}</th>
              <th class="border-top-0" v-if="currenciesDiffer">
                {{$t('Converted Amount')}}
              </th>
            </tr>
            <tr v-for="(invoicePayment, index) in model.invoice_payments" :key="`invoice-payment-row-${index}`">
              <td>
                <a href="javascript:void(0)"
                   @click="onInvoiceClick(invoicePayment.invoice)">
                  {{ invoicePayment.invoice.invoice_no }}
                </a>
              </td>
              <td>
                <currency-input-widget :currency="currencySymbol" :label="false"
                                       :model="invoicePayment" attribute="amount" @input="onAmountChange(invoicePayment)"/>
              </td>
              <td v-if="currenciesDiffer">
                <currency-input-widget :currency="invoiceCurrencySymbol" :label="false"
                                       :model="invoicePayment" attribute="converted_amount"
                                       @input="onConvertedAmountChange(invoicePayment)"/>
              </td>
            </tr>
            <tr v-if="showFeePercent && totalAmount">
              <td :colspan="currenciesDiffer ? 3 : 2" class="text-right border-top-0 pb-0">
                <span class="d-inline-block mr-3"><b>{{ $t('Surcharge') }}</b></span>
                <span>{{ getPaymentFeeAmount() | asCurrency(currencySymbol) }}</span>
              </td>
            </tr>
            <tr>
              <td :colspan="currenciesDiffer ? 3 : 2" class="text-right border-top-0 pb-0">
                <span class="d-inline-block mr-3"><b>{{ $t('Total Amount') }}</b></span>
                <span>{{ totalAmount | asCurrency(currencySymbol) }}</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <hr class="mt-2">

      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="method" value-field="value" @select2Select="onMethodSelect"
                        display-field="${item.key}" type="select2" :items="computedPaymentMethods"/>
        </div>
        <div class="col-md-6">
          <currency-select-input-widget :model="model" attribute="currency_uuid"
                                        @select="onCurrencySelect" :disabled="!!model.uuid"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="exchange_rate" :rules="exchangeRateRules" type="number"
                        @input="onExchangeRateChange"/>
        </div>
        <div class="col-md-6" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
          <input-widget :model="model" attribute="bank_account" value-field="value"
                        display-field="${item.key}" type="select2" :items="paymentBankAccounts"/>
        </div>
        <div class="col-md-6" v-if="hasAccountingCode">
          <input-widget :model="model" attribute="accounting_code" :items="paymentAccountingCodeOptions"
                        display-field="${item.key}" type="select2" value-field="value"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="reference_number" type="text" rules="max:11"/>
        </div>
        <div class="col-md-4">
          <input-widget is-switch :model="model" attribute="reconciled" type="checkbox"/>
        </div>
        <div class="col-md-4" v-if="showFeePercent">
          <input-widget :model="model" attribute="fee_percent" type="number" rules="min:0"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import PaymentModel from "./models/Payment.model";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import {SOURCE_INVOICE_PAYMENTS_TABLE, SOURCE_INVOICES_TABLE} from "../constants";
  import InvoicesService from "./InvoicesService";
  import InvoicePayment from "./models/InvoicePayment.model";
  import CustomerService from "@/modules/customer/CustomerService";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget";
  import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: invoicePaymentsActions} = createNamespacedHelpers('invoicePayments');

  export default {
    name: "PaymentFormModal",
    components: {
      CustomerEmailAddressInfo,
      CurrencySelectInputWidget,
      LiveSaleRef,
      CurrencyInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new PaymentModel(),
        customer: null,
        depositReservations: [],
        deposit: null,
        showFeePercent: false
      }
    },
    computed: {
      ...globalMapState({
        paymentMethods: state => state.paymentMethods,
        paymentAccountingCodeOptions: state => state.paymentAccountingCodeOptions,
        enableContinuousAuction: state => state.enableContinuousAuction,
        availablePaymentCurrencies: state => state.availablePaymentCurrencies,
        currencies: state => state.currencies,
        currentBranch: state => state.currentBranch,
        paymentBankAccounts: state => state.paymentBankAccounts,
      }),
      ...mapState({
        modal: state => state.paymentModal,
        editObject: state => state.paymentModal.object,
        type: state => state.paymentModal.type,
        invoices: state => state.paymentModal.invoices,
        totalAmount() {
          let sum = 0;
          this.model.invoice_payments.forEach(invoicePayment => sum += parseFloat(invoicePayment.amount));
          return sum;
        }
      }),
      computedPaymentMethods() {
        if (this.currenciesDiffer) {
          return this.paymentMethods.filter(p => p.value !== 'deposit')
        }

        return this.paymentMethods
      },
      currenciesDiffer() {
        return this.invoiceCurrency && this.currency && this.invoiceCurrency.uuid !== this.currency.uuid
      },
      invoiceCurrency() {
        if (this.model.invoice_payments && this.model.invoice_payments.length > 0 && this.model.invoice_payments[0].invoice) {
          return this.model.invoice_payments[0].invoice.currency;
        }
        return null
      },
      currency() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        return tmpCurrency ? tmpCurrency : null
      },
      currencySymbol() {
        return this.currency ? this.currency.symbol : null
      },
      invoiceCurrencySymbol() {
        return this.invoiceCurrency ? this.invoiceCurrency.symbol : null
      },
      modalTitle() {
        if (this.model.uuid) {
          if (this.type === 'refund') {
            return this.$i18n.t(`Update Refund`);
          }
          return this.$i18n.t(`Update Payment`);
        } else {
          if (this.type === 'refund') {
            return this.$i18n.t(`Create Refund`);
          }
          return this.$i18n.t(`Create Payment`);
        }
      },
      hasAccountingCode() {
        return this.paymentAccountingCodeOptions && this.paymentAccountingCodeOptions.length > 0;
      },
      exchangeRateRules() {
        return this.currenciesDiffer ? 'required' : ''
      }
    },
    watch: {
      editObject() {
        this.showFeePercent = false;
        if (this.editObject && this.editObject.uuid) {
          const invoices = [];
          this.editObject.invoice_payments.forEach(invoicePayment => invoices.push(invoicePayment.invoice));
          this.model = new PaymentModel(this.editObject);
          this.initExtraInfo(invoices)
        }
      },
      invoices() {
        this.invoices.forEach(invoice => {
          if (!this.model.currency_uuid) {
            this.model.currency_uuid = invoice.currency.uuid
          }
          let amount = 0;
          if (this.type === 'payment') {
            amount = parseFloat(invoice.outstanding_amount);
          } else if (this.type === 'refund') {
            amount = parseFloat(invoice.paid_amount * -1);
          }
          this.model.invoice_payments.push(new InvoicePayment({
            amount,
            invoice_uuid: invoice.uuid,
            invoice: invoice
          }));
        });
      },
    },
    methods: {
      ...mapActions(['hidePaymentModal', 'getTableData', 'updatePaymentListModalInvoice']),
      ...invoicePaymentsActions({invoicePaymentsGetTableData: 'getTableData'}),

      onShow() {
        this.showConfirmation = true;
        this.initExtraInfo(this.invoices);
      },
      onHidden() {
        this.customer = null;
        this.depositReservations = [];
        this.deposit = null;
        this.model = new PaymentModel();
        this.showFeePercent = false;
        this.hidePaymentModal();
      },
      async onSubmit() {
        if (this.model.hasError()) {
          return;
        }

        this.loading = true;

        this.model.type = this.type;
        const invoice_payments = this.model.invoice_payments.map(item => {
          return {
            amount: item.amount,
            invoice_uuid: item.invoice_uuid
          }
        });

        let response;

        if (!this.model.uuid) {
          response = await InvoicesService.createPayment({
            ...this.model.toJSON(),
            invoice_payments
          });
        } else {
          response = await InvoicesService.updatePayment(this.model.uuid, {
            ...this.model.toJSON(),
            invoice_payments
          });
        }

        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hidePaymentModal();
          let successMsg = this.type === 'refund' ? this.$t('Invoice(s) were successfully refunded') : this.$t('Invoice(s) were successfully paid')
          this.$toast('success', successMsg);
          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            await this.getTableData();
            this.updatePaymentListModalInvoice();
          } else if (this.modal.source === SOURCE_INVOICE_PAYMENTS_TABLE) {
            await this.invoicePaymentsGetTableData()
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      onMethodSelect(data) {
        this.model.resetErrors('method');
        if (data.value === 'deposit') {
          let depositInvoice = this.invoices.find(inv => inv.type === 'deposit');
          if (depositInvoice) {
            this.model.addError('method', this.$i18n.t("You can not pay deposit invoice with deposit payment"));
          }
        }

        if (this.hasAccountingCode && data.accounting_code && data.value !== 'deposit') {
          let accountingCode = this.paymentAccountingCodeOptions.find(c => c.value === data.accounting_code);
          if (accountingCode) {
            this.model.accounting_code = accountingCode.value;
          }
        }
        if (!this.model.uuid) {
          const paymentMethod = this.paymentMethods.find(p => p.value === data.value);
          if (paymentMethod.hasOwnProperty('fee_percent')) {
            this.showFeePercent = true;
            this.model.fee_percent = paymentMethod.fee_percent;
          } else {
            this.model.fee_percent = null;
          }
        }
      },
      async initExtraInfo(invoices) {
        if (!invoices || invoices.length === 0) {
          return;
        }

        let customerUuid;
        let currencyUuid;
        let saleNos = [];

        for (let invoice of invoices) {
          customerUuid = invoice.customer_uuid;
          currencyUuid = invoice.currency_uuid;
          if (invoice.auction && invoice.auction.sale_no) {
            saleNos.push(invoice.auction.sale_no);
          }
        }

        let response = await CustomerService.getCustomer(customerUuid, 'deposits.currency,deposit_reservations.currency,billing_address,deposit_reservations.auction,term',);

        if (response.success) {
          this.customer = response.body;
          this.deposit = response.body.deposits.find(d => d.currency_uuid === currencyUuid);
          if (saleNos.length > 0) {
            for (let depositReservation of response.body.deposit_reservations) {
              if (depositReservation.currency_uuid === currencyUuid && saleNos.includes(depositReservation.auction.sale_no)) {
                this.depositReservations.push(depositReservation);
              }
            }
          }

          if (!this.model.uuid && this.customer.term && this.customer.term.payment_method) {
            this.model.method = this.customer.term.payment_method
          }
        }
      },
      async onInvoiceClick(invoice) {
        await InvoicesService.viewInvoicePdf(this, invoice, true)
      },
      onCurrencySelect() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        let exchangeRate = null;

        for (let m of this.model.invoice_payments) {
          if (!m.invoice) {
            continue;
          }
          let invoiceType = m.invoice.type;

          if (tmpCurrency.uuid === this.invoiceCurrency.uuid) {
            exchangeRate = 1
          } else if (invoiceType === 'manual' || invoiceType === 'service') {
            if (this.currentBranch.currency_exchanges && this.currentBranch.currency_exchanges[tmpCurrency.code]) {
              exchangeRate = this.currentBranch.currency_exchanges[tmpCurrency.code]
            }
          } else if (invoiceType === 'auction') {
            if (m.invoice.auction && m.invoice.auction.currency_exchanges && m.invoice.auction.currency_exchanges[tmpCurrency.code]) {
              exchangeRate = m.invoice.auction.currency_exchanges[tmpCurrency.code]
            }
          }

          if (exchangeRate !== null) {
            break
          }
        }

        this.model.exchange_rate = exchangeRate
        this.calculateAmountAll()
      },
      calculateAmountAll() {
        for (let invoicePayment of this.model.invoice_payments) {
          this.calculateAmount(invoicePayment)
        }
      },
      calculateAmount(invoicePayment, amountSource = null) {
        if (amountSource === null) {
          amountSource = invoicePayment.initial_amount
        }

        if (this.model.exchange_rate) {
          invoicePayment.amount = (amountSource / this.model.exchange_rate).toFixed(2)
        }
        invoicePayment.converted_amount = amountSource
      },
      onExchangeRateChange() {
        this.$nextTick(() => {
          this.calculateAmountAll()
        })
      },
      onAmountChange(invoicePayment) {
        if (!this.currenciesDiffer || !this.model.exchange_rate) {
          return true
        }
        this.$nextTick(() => {
          invoicePayment.converted_amount = (invoicePayment.amount * this.model.exchange_rate).toFixed(2)
        })
      },
      onConvertedAmountChange(invoicePayment) {
        this.calculateAmount(invoicePayment, invoicePayment.converted_amount)
      },
      getPaymentFeeAmount() {
        if (!this.totalAmount || !this.model.fee_percent) {
          return 0;
        }
        return this.totalAmount - Math.round((this.totalAmount/(1 + this.model.fee_percent/100) * 100)) / 100;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .invoice-payments-table {
    tbody {
      td {
        vertical-align: middle;
        padding-top: .7em;
        padding-bottom: .7em;
      }
    }

    /deep/ .form-group {
      margin-bottom: 0;
    }
  }
</style>
